interface Person {
  '@type': 'Person'
  'name': string
  'url'?: string
}

interface Organization {
  '@type': 'Organization'
  'name': string
  'url': string
  'logo'?: {
    '@type': 'ImageObject'
    'url': string
  }
}

interface ImageObject {
  '@type': 'ImageObject'
  'url': string
  'width'?: number
  'height'?: number
}

interface Article {
  '@context': 'https://schema.org'
  '@type': 'Article'
  'headline': string
  'description': string
  'author': Person | Organization
  'publisher': Organization
  'datePublished': string
  'dateModified'?: string
  'image'?: ImageObject
  'url': string
  'mainEntityOfPage': {
    '@type': 'WebPage'
    '@id': string
  }
}

interface BlogPostingArticle extends Omit<Article, '@type'> {
  '@type': 'BlogPosting'
}

interface BlogPostData {
  title: string
  description: string
  slug: string
  published_at?: string
  updated_at?: string
  cover?: {
    url?: string
    formats?: {
      large?: { url?: string, width?: number, height?: number }
      medium?: { url?: string, width?: number, height?: number }
    }
  }
  author?: {
    name?: string
    url?: string
  }
}

export function generateArticleSchema(blogPost: BlogPostData): Article {
  const baseUrl = 'https://employers.io'
  const articleUrl = `${baseUrl}/blog/${blogPost.slug}`

  // Get the best available image
  let image: ImageObject | undefined
  if (blogPost.cover?.formats?.large?.url) {
    image = {
      '@type': 'ImageObject',
      'url': blogPost.cover.formats.large.url.startsWith('http')
        ? blogPost.cover.formats.large.url
        : `${baseUrl}${blogPost.cover.formats.large.url}`,
      'width': blogPost.cover.formats.large.width,
      'height': blogPost.cover.formats.large.height
    }
  } else if (blogPost.cover?.url) {
    image = {
      '@type': 'ImageObject',
      'url': blogPost.cover.url.startsWith('http')
        ? blogPost.cover.url
        : `${baseUrl}${blogPost.cover.url}`
    }
  }

  // Author information
  const author: Person | Organization = blogPost.author?.name
    ? {
        '@type': 'Person',
        'name': blogPost.author.name,
        'url': blogPost.author.url
      }
    : {
        '@type': 'Organization',
        'name': 'employers.io',
        'url': baseUrl
      }

  // Publisher information
  const publisher: Organization = {
    '@type': 'Organization',
    'name': 'employers.io',
    'url': baseUrl,
    'logo': {
      '@type': 'ImageObject',
      'url': `${baseUrl}/img/logo.svg`
    }
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': blogPost.title,
    'description': blogPost.description,
    author,
    publisher,
    'datePublished': blogPost.published_at || new Date().toISOString(),
    'dateModified': blogPost.updated_at,
    image,
    'url': articleUrl,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': articleUrl
    }
  }
}

export function generateBlogPostingSchema(blogPost: BlogPostData): BlogPostingArticle {
  const articleSchema = generateArticleSchema(blogPost)
  return {
    ...articleSchema,
    '@type': 'BlogPosting'
  }
}
